import './Navigation.css';
import { FaRegRectangleXmark } from 'react-icons/fa6';
import { useContext, useEffect, useState } from 'react';
import AppContext from '../../AppContext';
import ReportView from './ReportView/ReportView';
import ExternalLink from './Links/ExternalLink';
import Environment from '../../Environment';
import DashboardLink from './Links/DashboardLink';
import Spinner from '../Loading/Spinner/Spinner';
import SupportingDocumentLink from './Links/SupportingDocumentLink';
import { RiskRepositoryCode } from './Links/RiskRepositoryCode';

export default function Navigation() {
  const {
    setNavIsOpen,
    cocode,
    year,
    entityId,
    statementType,
    securityCIK,
    groupCode,
    periodType
  } = useContext(AppContext);
  const [statementTypeCode, setStatementTypeCode] = useState('');
  const [lowerCaseStatementTypeCode, setLowerCaseStatementTypeCode] = useState('');

  // In quarterly mode, hide the child links under each risk profile.
  // Group Profile and Other General Links sections remain visible.
  // Tracked in TP: 56437 - EP quarterly dashboard links under risk
  const showRiskProfileChildren = !periodType;

  useEffect(() => {
    if (statementType && statementType.statementTypeCode) {
      setStatementTypeCode(statementType.statementTypeCode);
      setLowerCaseStatementTypeCode(statementType.statementTypeCode.toLowerCase());
    }
  }, [statementType]);

  if (!statementTypeCode) {
    return (
      <div className="nav_container loading_container">
        <Spinner />
      </div>
    );
  } else {
    return (
      <div className="nav_container">
        <div className="nav_title">
          <div>Menu</div>
          <FaRegRectangleXmark
            size={'1.5rem'}
            onClick={() => setNavIsOpen(false)}
            aria-label="Close"
            id="close_nav"
          />
        </div>
        <div className="nav_options">
          <ul>
            <ReportView reportViewCode="FS">
              {showRiskProfileChildren && (
              <ul>
                <li>
                  <ExternalLink
                    url={`https://bestlink.ambest.com/CompanyRedirect.aspx?NAICNum=${cocode}`}
                    linkText={'AM Best'}
                  />
                </li>
                <li className="dashboard_list">
                  Dashboards
                  <ul>
                    <li>
                      <DashboardLink
                        linkText={'Cash Flow'}
                        relativeUrl={`&SUBD=${lowerCaseStatementTypeCode}cashflow`}
                      />
                    </li>
                    <li>
                      <DashboardLink
                        linkText={'Mix of Business'}
                        relativeUrl={`&SUBD=${lowerCaseStatementTypeCode}writings`}
                      />
                    </li>
                    <li>
                      <DashboardLink
                        linkText={'Performance'}
                        relativeUrl={`&SUBD=${lowerCaseStatementTypeCode}performance`}
                      />
                    </li>
                    <li>
                      <DashboardLink
                        linkText={'RBC Summary'}
                        relativeUrl={`&SUBD=${lowerCaseStatementTypeCode}rbcsummary`}
                      />
                    </li>
                    <li>
                      <DashboardLink
                        linkText={'Scoring'}
                        relativeUrl={`&SUBD=${lowerCaseStatementTypeCode}scoring`}
                      />
                    </li>
                  </ul>
                </li>
                <li>
                  <ExternalLink
                    url={`${Environment.ISITE_URL()}/iris-worksheet/IrisWorksheetServlet?REPORT=IRISWorksheet&CATEGORY=IRIS&SCREPID=0&CAPP=IST&COCODE=${statementTypeCode}${cocode}&DATA_YEAR=${year}`}
                    linkText={'IRIS'}
                  />
                </li>
                {(statementTypeCode === 'L' || statementTypeCode === 'P') && (
                  <li>
                    <ExternalLink
                      url={`${Environment.ISITE_URL()}/peer/PAFPMaster?&FORMAT=HTML&SOURCE=FRED&COCODE=${statementTypeCode}${cocode}&DATA_YEAR=${year}`}
                      linkText={'Peer Report'}
                    />
                  </li>
                )}
                <li>
                  <ExternalLink
                    url={`${Environment.ISITE_URL()}/scoring/ScoMasterServlet?REPORT=04&CATEGORY=SCORING&COCODE=${statementTypeCode}${cocode}&DATA_YEAR=${year}`}
                    linkText={'Scoring Report'}
                  />
                </li>
              </ul>
              )}
            </ReportView>
            <ReportView reportViewCode="CR">
              {showRiskProfileChildren && (
              <ul>
                <li>
                  <SupportingDocumentLink
                    year={year}
                    riskRepositoryCode={RiskRepositoryCode.CREDIT}
                    statementTypeCode={statementTypeCode}
                  />
                </li>
                <li className="dashboard_list">
                  Dashboards
                  <ul>
                    <li>
                      <DashboardLink linkText={'BA Assets'} relativeUrl={`&SUBD=baassets`} />
                    </li>
                    <li>
                      <DashboardLink
                        linkText={'Bonds'}
                        relativeUrl={`&SUBD=${lowerCaseStatementTypeCode}bonds`}
                      />
                    </li>
                    <li>
                      <DashboardLink
                        linkText={'Climate Affected Assets'}
                        relativeUrl={`&SUBD=${lowerCaseStatementTypeCode}climate&DYEAR=${year}`}
                      />
                    </li>
                    <li>
                      <DashboardLink
                        linkText={'Counterparty Exposure'}
                        relativeUrl={`&SUBD=${lowerCaseStatementTypeCode}exposure&DYEAR=${year}`}
                      />
                    </li>
                    {statementTypeCode === 'L' ? (
                      <li>
                        <DashboardLink
                          linkText={'Derivatives'}
                          relativeUrl={`&SUBD=${lowerCaseStatementTypeCode}derivatives`}
                        />
                      </li>
                    ) : (
                      ''
                    )}
                    <li>
                      <DashboardLink linkText={'Equities'} relativeUrl={`&SUBD=equities`} />
                    </li>
                    <li>
                      <DashboardLink
                        linkText={'Mortgage and Real Estate'}
                        relativeUrl={`&SUBD=mortloans`}
                      />
                    </li>
                    <li>
                      <DashboardLink
                        linkText={'Reinsurance'}
                        relativeUrl={`&SUBD=${lowerCaseStatementTypeCode}reins`}
                      />
                    </li>
                  </ul>
                </li>
                <li>
                  <ExternalLink
                    linkText={'Investment Jumpstart Report'}
                    url={`${Environment.ISITE_URL()}/exam-jumpstart/Company?cocode=${cocode}&year=${year}`}
                  />
                </li>
                <li>
                  <ExternalLink
                    linkText={'Snapshot Investment Summary'}
                    url={`${Environment.ISITE_URL()}/ssr/Snapshot?cocode=${cocode}&year=${year}&type=0&reportId=1`}
                  />
                </li>
              </ul>
              )}
            </ReportView>
            <ReportView reportViewCode="LG">
              {showRiskProfileChildren && (
              <ul>
                <li>
                  <ExternalLink
                    url={`https://bestlink.ambest.com/CompanyRedirect.aspx?NAICNum=${cocode}`}
                    linkText={'AM Best'}
                  />
                </li>
                <li className="dashboard_list">
                  Dashboards
                  <ul>
                    <li>
                      <ExternalLink
                        linkText={'RIRS (Tableau)'}
                        url={`${Environment.ISITE_URL()}/mis-isite/rirs/tableau/rirsCountAnalytics.seam?cocode=${cocode}`}
                        showIcon={false}
                      />
                    </li>
                  </ul>
                </li>
                <li>
                  <SupportingDocumentLink
                    year={year}
                    riskRepositoryCode={RiskRepositoryCode.LEGAL}
                    statementTypeCode={statementTypeCode}
                  />
                </li>
                <li>
                  <ExternalLink
                    linkText={'Market Analysis Profile'}
                    url={`${Environment.ISITE_URL()}/MRJump/servlet/org.isite.mrjump.MRJumpServlet?cocode=${cocode}&sEntityID=${entityId}`}
                  />
                </li>
                <li>
                  <ExternalLink
                    linkText={'RIRS'}
                    url={`${Environment.ISITE_URL()}/mis-isite/rirs/entity/entityReport.seam?entityId=${entityId}`}
                  />
                </li>
              </ul>
              )}
            </ReportView>
            <ReportView reportViewCode="LQ">
              {showRiskProfileChildren && (
              <ul>
                <li className="dashboard_list">
                  Dashboards
                  <ul>
                    <li>
                      <DashboardLink linkText={'BA Assets'} relativeUrl={`&SUBD=baassets`} />
                    </li>
                    <li>
                      <DashboardLink
                        linkText={'Bonds'}
                        relativeUrl={`&SUBD=${lowerCaseStatementTypeCode}bonds`}
                      />
                    </li>
                    <li>
                      <DashboardLink linkText={'Equities'} relativeUrl={`&SUBD=equities`} />
                    </li>
                    <li>
                      <DashboardLink
                        linkText={'Mortgage and Real Estate'}
                        relativeUrl={`&SUBD=mortloans`}
                      />
                    </li>
                  </ul>
                </li>
                <li>
                  <SupportingDocumentLink
                    year={year}
                    riskRepositoryCode={RiskRepositoryCode.LIQUIDITY}
                    statementTypeCode={statementTypeCode}
                  />
                </li>
                <li>
                  <ExternalLink
                    linkText={'Snapshot Investment Summary'}
                    url={`${Environment.ISITE_URL()}/ssr/Snapshot?cocode=${cocode}&year=${year}&type=0&reportId=1`}
                  />
                </li>
              </ul>
              )}
            </ReportView>
            <ReportView reportViewCode="MK">
              {showRiskProfileChildren && (
              <ul>
                <li className="dashboard_list">
                  Dashboards
                  <ul>
                    <li>
                      <DashboardLink linkText={'BA Assets'} relativeUrl={`&SUBD=baassets`} />
                    </li>
                    <li>
                      <DashboardLink
                        linkText={'Bonds'}
                        relativeUrl={`&SUBD=${lowerCaseStatementTypeCode}bonds`}
                      />
                    </li>

                    <li>
                      <DashboardLink
                        linkText={'Climate Affected Assets'}
                        relativeUrl={`&SUBD=${lowerCaseStatementTypeCode}climate&DYEAR=${year}`}
                      />
                    </li>
                    <li>
                      <DashboardLink
                        linkText={'Counterparty Exposure'}
                        relativeUrl={`&SUBD=${lowerCaseStatementTypeCode}exposure&DYEAR=${year}`}
                      />
                    </li>
                    {statementTypeCode === 'L' ? (
                      <li>
                        <DashboardLink
                          linkText={'Derivatives'}
                          relativeUrl={`&SUBD=${lowerCaseStatementTypeCode}derivatives`}
                        />
                      </li>
                    ) : (
                      ''
                    )}
                    <li>
                      <DashboardLink linkText={'Equities'} relativeUrl={`&SUBD=equities`} />
                    </li>
                    <li>
                      <DashboardLink
                        linkText={'Mortgage and Real Estate'}
                        relativeUrl={`&SUBD=mortloans`}
                      />
                    </li>
                  </ul>
                </li>
                <li>
                  <ExternalLink
                    linkText={'Investment Jumpstart Report'}
                    url={`${Environment.ISITE_URL()}/exam-jumpstart/Company?cocode=${cocode}&year=${year}`}
                  />
                </li>
                <li>
                  <SupportingDocumentLink
                    year={year}
                    riskRepositoryCode={RiskRepositoryCode.MARKET}
                    statementTypeCode={statementTypeCode}
                  />
                </li>
                <li>
                  <ExternalLink
                    linkText={'Snapshot Investment Summary'}
                    url={`${Environment.ISITE_URL()}/ssr/Snapshot?cocode=${cocode}&year=${year}&type=0&reportId=1`}
                  />
                </li>
              </ul>
              )}
            </ReportView>
            <ReportView reportViewCode="OP">
              {showRiskProfileChildren && (
              <ul>
                <li className="dashboard_list">
                  Dashboards
                  <ul>
                    <li>
                      <DashboardLink
                        linkText={'Operating Performance/Expenses'}
                        relativeUrl={`&SUBD=${lowerCaseStatementTypeCode}expense`}
                      />
                    </li>
                  </ul>
                  <ul>
                    {statementTypeCode === 'L' ? (
                      <li>
                        <DashboardLink
                          relativeUrl={`&SUBD=${lowerCaseStatementTypeCode}separateaccounts`}
                          linkText={'Separate Accounts'}
                        />
                      </li>
                    ) : (
                      ''
                    )}
                  </ul>
                </li>
                <li>
                  <SupportingDocumentLink
                    year={year}
                    riskRepositoryCode={RiskRepositoryCode.OPERATIONAL}
                    statementTypeCode={statementTypeCode}
                  />
                </li>
              </ul>
              )}
            </ReportView>
            <ReportView reportViewCode="PU">
              {showRiskProfileChildren && (
              <ul>
                <li className="dashboard_list">
                  Dashboards
                  <ul>
                    <li>
                      <DashboardLink
                        linkText={'Mix of Business'}
                        relativeUrl={`&SUBD=${lowerCaseStatementTypeCode}writings`}
                      />
                    </li>
                    <li>
                      <DashboardLink
                        linkText={'Reinsurance'}
                        relativeUrl={`&SUBD=${lowerCaseStatementTypeCode}reins`}
                      />
                    </li>
                  </ul>
                </li>
                {(statementTypeCode === 'L' || statementTypeCode === 'P') && (
                  <li>
                    <ExternalLink
                      url={`${Environment.ISITE_URL()}/peer/PAFPMaster?&FORMAT=HTML&SOURCE=FRED&COCODE=${statementTypeCode}${cocode}&DATA_YEAR=${year}`}
                      linkText={'Peer Report'}
                    />
                  </li>
                )}
                <li>
                  <SupportingDocumentLink
                    year={year}
                    riskRepositoryCode={RiskRepositoryCode.PRICING_AND_UNDERWRITING}
                    statementTypeCode={statementTypeCode}
                  />
                </li>
              </ul>
              )}
            </ReportView>
            <ReportView reportViewCode="RP">
              {showRiskProfileChildren && (
              <ul>
                <li>
                  <ExternalLink
                    url={`https://bestlink.ambest.com/CompanyRedirect.aspx?NAICNum=${cocode}`}
                    linkText={'AM Best'}
                  />
                </li>
                <li className="dashboard_list">
                  Dashboards
                  <ul>
                    <li>
                      <ExternalLink
                        linkText={'Complaints Count Analytics (Tableau)'}
                        url={`${Environment.ISITE_URL()}/mis-isite/cds/tableau/cdsCountAnalytics.seam?cocode=${cocode}`}
                        showIcon={false}
                      />
                    </li>
                    <li>
                      <ExternalLink
                        linkText={'Complaints Index Analytics (Tableau)'}
                        url={`${Environment.ISITE_URL()}/mis-isite/cds/tableau/cdsIndexAnalytics.seam?cocode=${cocode}`}
                        showIcon={false}
                      />
                    </li>
                  </ul>
                </li>
                <li>
                  <ExternalLink
                    linkText={'Market Analysis Profile'}
                    url={`${Environment.ISITE_URL()}/MRJump/servlet/org.isite.mrjump.MRJumpServlet?cocode=${cocode}&sEntityID=${entityId}`}
                  />
                </li>
                <li>
                  <SupportingDocumentLink
                    year={year}
                    riskRepositoryCode={RiskRepositoryCode.REPUTATIONAL}
                    statementTypeCode={statementTypeCode}
                  />
                </li>
                <li>
                  <ExternalLink linkText={'StockCharts'} url={`https://stockcharts.com/`} />
                </li>
              </ul>
              )}
            </ReportView>
            <ReportView reportViewCode="RV">
              {showRiskProfileChildren && (
              <ul>
                <li className="dashboard_list">
                  Dashboards
                  <ul>
                    <li>
                      <DashboardLink
                        linkText={'Mix of Business'}
                        relativeUrl={`&SUBD=${lowerCaseStatementTypeCode}writings`}
                      />
                    </li>
                    <li>
                      <DashboardLink
                        linkText={'Reserves'}
                        relativeUrl={`&SUBD=${lowerCaseStatementTypeCode}reserves`}
                      />
                    </li>
                  </ul>
                </li>
                <li>
                  <SupportingDocumentLink
                    year={year}
                    statementTypeCode={statementTypeCode}
                    riskRepositoryCode={RiskRepositoryCode.RESERVING}
                  />
                </li>
                <li>
                  <ExternalLink
                    url={`${Environment.ISITE_URL()}/loss-reserves/LossReservesSchP`}
                    linkText={'Industry Data - Aggregate Schedule P Data'}
                  />
                </li>
              </ul>
              )}
            </ReportView>
            <ReportView reportViewCode="ST">
              {showRiskProfileChildren && (
              <ul>
                <li className="dashboard_list">
                  Dashboards
                  <ul>
                    <li>
                      <DashboardLink
                        linkText={'Mix of Business'}
                        relativeUrl={`&SUBD=${lowerCaseStatementTypeCode}writings`}
                      />
                    </li>
                    <li>
                      <DashboardLink
                        linkText={'Reinsurance'}
                        relativeUrl={`&SUBD=${lowerCaseStatementTypeCode}reins`}
                      />
                    </li>
                  </ul>
                </li>
                <li>
                  <ExternalLink
                    linkText={'Form A'}
                    url={`${Environment.ISITE_URL()}/forma/views/formA.htm`}
                  />
                </li>
                <li>
                  <ExternalLink
                    linkText={'Snapshot Investment Summary'}
                    url={`${Environment.ISITE_URL()}/ssr/Snapshot?cocode=${cocode}&year=${year}&type=0&reportId=1`}
                  />
                </li>
                <li>
                  <SupportingDocumentLink
                    statementTypeCode={statementTypeCode}
                    year={year}
                    riskRepositoryCode={RiskRepositoryCode.STRATEGIC}
                  />
                </li>
              </ul>
              )}
            </ReportView>
            <ReportView reportViewCode="FR">
              {showRiskProfileChildren && (
              <ul>
                <li className="dashboard_list">
                  Dashboards
                  <ul>
                    <li>
                      <DashboardLink linkText={'BA Assets'} relativeUrl={`&SUBD=baassets`} />
                    </li>
                    <li>
                      <DashboardLink
                        linkText={'Bonds'}
                        relativeUrl={`&SUBD=${lowerCaseStatementTypeCode}bonds`}
                      />
                    </li>
                    <li>
                      <DashboardLink
                        linkText={'Cash Flow'}
                        relativeUrl={`&SUBD=${lowerCaseStatementTypeCode}cashflow`}
                      />
                    </li>
                    <li>
                      <DashboardLink
                        linkText={'Climate Affected Assets'}
                        relativeUrl={`&SUBD=${lowerCaseStatementTypeCode}climate&DYEAR=${year}`}
                      />
                    </li>
                    <li>
                      <ExternalLink
                        linkText={'Complaints Count Analytics (Tableau)'}
                        url={`${Environment.ISITE_URL()}/mis-isite/cds/tableau/cdsCountAnalytics.seam?cocode=${cocode}`}
                        showIcon={false}
                      />
                    </li>
                    <li>
                      <ExternalLink
                        linkText={'Complaints Index Analytics (Tableau)'}
                        url={`${Environment.ISITE_URL()}/mis-isite/cds/tableau/cdsIndexAnalytics.seam?cocode=${cocode}`}
                        showIcon={false}
                      />
                    </li>
                    <li>
                      <DashboardLink
                        linkText={'Counterparty Exposure'}
                        relativeUrl={`&SUBD=${lowerCaseStatementTypeCode}exposure&DYEAR=${year}`}
                      />
                    </li>
                    {statementTypeCode === 'L' ? (
                      <li>
                        <DashboardLink
                          linkText={'Derivatives'}
                          relativeUrl={`&SUBD=${lowerCaseStatementTypeCode}derivatives`}
                        />
                      </li>
                    ) : (
                      ''
                    )}
                    <li>
                      <DashboardLink linkText={'Equities'} relativeUrl={`&SUBD=equities`} />
                    </li>
                    <li>
                      <DashboardLink
                        linkText={'Mix of Business'}
                        relativeUrl={`&SUBD=${lowerCaseStatementTypeCode}writings`}
                      />
                    </li>
                    <li>
                      <DashboardLink
                        linkText={'Mortgage and Real Estate'}
                        relativeUrl={`&SUBD=mortloans`}
                      />
                    </li>
                    <DashboardLink
                      linkText={'Operating Performance/Expenses'}
                      relativeUrl={`&SUBD=${lowerCaseStatementTypeCode}expense`}
                    />
                    <li>
                      <DashboardLink
                        linkText={'Performance'}
                        relativeUrl={`&SUBD=${lowerCaseStatementTypeCode}performance`}
                      />
                    </li>
                    <li>
                      <DashboardLink
                        linkText={'RBC Summary'}
                        relativeUrl={`&SUBD=${lowerCaseStatementTypeCode}rbcsummary`}
                      />
                    </li>
                    <li>
                      <DashboardLink
                        linkText={'Reinsurance'}
                        relativeUrl={`&SUBD=${lowerCaseStatementTypeCode}reins`}
                      />
                    </li>
                    <li>
                      <DashboardLink
                        linkText={'Reserves'}
                        relativeUrl={`&SUBD=${lowerCaseStatementTypeCode}reserves`}
                      />
                    </li>
                    <li>
                      <ExternalLink
                        linkText={'RIRS (Tableau)'}
                        url={`${Environment.ISITE_URL()}/mis-isite/rirs/tableau/rirsCountAnalytics.seam?cocode=${cocode}`}
                        showIcon={false}
                      />
                    </li>
                    <li>
                      <DashboardLink
                        linkText={'Scoring'}
                        relativeUrl={`&SUBD=${lowerCaseStatementTypeCode}scoring`}
                      />
                    </li>
                    {statementTypeCode === 'L' ? (
                      <li>
                        <DashboardLink
                          linkText={'Separate Accounts'}
                          relativeUrl={`&SUBD=${lowerCaseStatementTypeCode}separateaccounts`}
                        />
                      </li>
                    ) : (
                      ''
                    )}
                  </ul>
                </li>
                {year >= 2024 ? (
                  <li className="dashboard_list">
                    <a
                      href={`${Environment.ISITE_URL()}/secure/fah/${year}/Profile Risk Assessments/Industry Averages/${year} Ind Avgs - All Business Types.xlsx`}
                      target="_blank"
                      rel="noreferrer">
                      {`Industry Averages`}
                    </a>
                  </li>
                ) : (
                  ''
                )}
                {statementTypeCode === 'X' ? (
                  <li>
                    <ExternalLink
                      url={`${Environment.ISITE_URL()}/secure/profiles/${year}/HealthZscore.pdf`}
                      linkText={'Z-Score Analysis'}
                    />
                  </li>
                ) : (
                  ''
                )}
              </ul>
              )}
            </ReportView>
            <ReportView reportViewCode="AC"></ReportView>
          </ul>
          <div className="other_links">
            <div className="other_links_title">
              <ExternalLink
                linkText={'Group Profile'}
                url={`${Environment.ISITE_URL()}/profile/GroupProfileCriteria?YEAR=${year}&COCODES=${cocode}`}
              />
            </div>
            <ExternalLink
              linkText="Group FEETS"
              url={`${Environment.ISITE_URL()}/feets-web/spring/feets/viewGroupExam?groupcode=${groupCode}`}
            />
            <ExternalLink
              linkText="Lead State Report"
              url={`${Environment.ISITE_URL()}/lead-state/LeadStateCriteria`}
            />
            <ExternalLink
              linkText="SEC Edgar Filings"
              url={`https://www.sec.gov/edgar/browse/?CIK=${securityCIK}`}
            />
            <ExternalLink
              linkText="Supervisory College Calendar"
              url={`${Environment.EAPPS_URL()}/supervisory-college-calendar/faces/pages/home.xhtml`}
            />
          </div>
          <div className="other_links">
            <div className="other_links_title">Other General Links</div>
            <ExternalLink
              linkText="CIPR Insurance Topics"
              url="https://content.naic.org/cipr_key_issues.htm"
            />
            <ExternalLink
              linkText="Industry Analysis Reports"
              url="https://content.naic.org/cipr-topics/insurance-industry-snapshots-and-analysis-reports"
            />
            <ExternalLink
              linkText="NAIC Publications"
              url="https://community.naic.org/media_center/folder/2e1cae6d-5944-4ff1-9575-0726f5ea994d"
            />
            <ExternalLink
              linkText="Risk Alert"
              url="https://community.naic.org/topics/37438/media_center/file/1d5836a6-79db-49b3-9259-cdf3182c5d43"
            />
            <ExternalLink
              linkText="Financial Regulator Resources"
              url="https://community.naic.org/topics/37438/media_center"
            />
          </div>
        </div>
      </div>
    );
  }
}
